import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { startCase, camelCase } from 'lodash';
import { maskUsername } from "../../SysGuard/SysGuardCommonComp";
import { GET_KEY_DOC_OWNER_DATA } from '../Utility/DataguardConstant';
import Page_Loader from '../../../assets/vizr_images/page_loader.gif';
import ReactECharts from 'echarts-for-react';
import './SmallHeatmap.scss';
import { formatNumber } from '../Utility/DataguardConstant';

const cellTotalWidth = 55;

const SmallHeatmap = () => {
  const dispatch = useDispatch();
  const keyDocOwnerData = useSelector((state) => state.DataguardReducer.keyDocOwnerData);
  const keyDocOwnerLoader = useSelector((state) => state.DataguardReducer.keyDocOwnerLoader);
  const isIncognito = useSelector((state) => state.DataguardReducer.incognito);
  const fromTimeStamp = useSelector((state) => state.DataguardReducer.fromTimeStamp);
  const toTimeStamp = useSelector((state) => state.DataguardReducer.toTimeStamp);

  useEffect(() => {
    if (fromTimeStamp && toTimeStamp) {
      dispatch({
        type: GET_KEY_DOC_OWNER_DATA,
        payload: {
          fromTimeStamp,
          toTimeStamp
        }
      });
    }
  }, [dispatch, fromTimeStamp, toTimeStamp]);

  const top3Users = useMemo(() => {
    if (!keyDocOwnerData || keyDocOwnerData.length === 0) return [];

    // Sort by totalPdaAccesses in descending order and take top 3
    return keyDocOwnerData
      .sort((a, b) => b.totalPdaAccesses - a.totalPdaAccesses)
      .slice(0, 3)
      .map((item, index) => ({
        ...item,
        displayName: isIncognito
          ? maskUsername(item.username)
          : startCase(camelCase(item.username)),
        rank: index + 1
      }));
  }, [keyDocOwnerData, isIncognito]);

  const getInitials = (username) => {
    return username
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  const maxAccesses = useMemo(() => {
    return top3Users.length > 0 ? Math.max(...top3Users.map(user => user.totalPdaAccesses)) : 0;
  }, [top3Users]);

  const getOption = () => {
    if (!top3Users.length) return {};

    // Transform data for ECharts heatmap
    const data = top3Users.map((user, index) => {
      return {
        value: [
          index, // x-axis position
          0, // y-axis position (single row)
          user.totalPdaAccesses, // value for color mapping
          getInitials(user.displayName) // initials for label
        ]
      };
    });

    return {
      tooltip: {
        position: 'top',
        formatter: function (params) {
          const user = top3Users[params.dataIndex];
          return `User: ${user.displayName}<br>Accesses: ${params.value[2].toLocaleString('en-US')}`;
        }
      },
      grid: {
        top: '10%',
        bottom: '10%',
        left: '10%',
        right: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        splitArea: { show: false },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        data: top3Users.map((_, i) => i)
      },
      yAxis: {
        type: 'category',
        splitArea: { show: false },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        data: [0]
      },
      visualMap: {
        show: false,
        dimension: 2, // Use value[2] (totalPdaAccesses) for coloring
        min: 0,
        max: maxAccesses,
        inRange: {
          color: ['#ffcccc', '#ff0000'] // Light red to dark red gradient
        },
        calculable: true,
        realtime: true
      },
      series: [{
        name: 'User Activity',
        type: 'heatmap',
        data: data,
        cellSize: [cellTotalWidth, cellTotalWidth],
        label: {
          show: true,
          position: 'inside',
          formatter: function (params) {
            return `${params.value[3]}\n${formatNumber(params.value[2])}`;
          },
          fontSize: 12,
          fontWeight: 'bold',
          color: '#FFFFFF'
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: '#fff'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    };
  };

  const renderSmallHeatmap = () => {
    if (keyDocOwnerLoader) {
      return (
        <div className="small-heatmap-container">
          <div className='loader-container'>
            <img src={Page_Loader} alt="Loading..." className='loader-img' />
          </div>
        </div>
      );
    } else if (!keyDocOwnerData || keyDocOwnerData.length === 0) {
      return (
        <div className="small-heatmap-container">
          <div className="no-data">No data available</div>
        </div>
      );
    } else {
      // Calculate chart width based on number of cells and cell size
      const chartWidth = top3Users.length * cellTotalWidth;
      const chartHeight = cellTotalWidth;
      
      return (
        <div className="small-heatmap-container">
          <ReactECharts
            option={getOption()}
            style={{
              height: `${chartHeight}px`,
              width: `${chartWidth}px`
            }}
            notMerge={true}
            opts={{
              renderer: 'canvas'
            }}
          />
        </div>
      );
    }
  }

  return (
    <>
      <div className='small-heatmap-wrapper'>
        <div className='ico-label' title="Top 3 Data Owners with Highest File Access">
          <span>Top 3 Data Owners</span>
        </div>
        {renderSmallHeatmap()}
      </div>
    </>
  );
};

export default SmallHeatmap;